import React, { useEffect, useState } from 'react';
import { Colors, Text } from 'library';
import { Grid, styled, useMediaQuery } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import theme from 'theme';
import { client, consoleLogInDev, track } from 'shared';
import { HomeFindSection } from './HomeFindSection';
import { StaffNearYou } from './StaffNearYou';
import { useHistory } from 'react-router';
import useQueryParams from 'shared/QueryParamsHook';
import { ITeacher } from 'models/Providers/Teacher';
import { StaffFilterOptions, defaultFilters } from './StaffFilter';

interface SpotlightWorkersResponse {
    workers: ITeacher[];
    vetted_workers_count: number;
}

function TrustItem({ text }: { text: string }) {
    return (
        <TrustRow>
            <CheckCircle style={{ color: Colors.turquoise, fontSize: 22, marginRight: 10 }} />
            <Text variant="body2" textStyle={{ color: Colors.darkNavy }}>{text}</Text>
        </TrustRow>
    );
}

export function HomeHeader({ signUp }: { signUp: () => void }) {
    const smDown = useMediaQuery(theme.breakpoints.down('md'));
    const history = useHistory();
    const query = useQueryParams();
    const searchedZipCode = query.get('zip');
    const queryFilters = query.get('filters');

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [zipCode, setZipCode] = useState<string>(query.get('zip') || '');
    const [spotlightWorkers, setSpotlightWorkers] = useState<ITeacher[]>([]);
    const [vettedWorkersCount, setVettedWorkersCount] = useState<number>(100);
    const [filters, setFilters] = useState<StaffFilterOptions>(
        queryFilters ? JSON.parse(atob(queryFilters)) : defaultFilters,
    );
    useEffect(() => {
        if (!searchedZipCode) {
            return;
        }
        setIsLoading(true);
        setSpotlightWorkers([]);
        setVettedWorkersCount(100);
        client(
            `api/contact-info-location/spotlighted-workers-by-location/?zip_code=${zipCode}${getEncodedFiltersQueryString(filters)}`,
        )
            .then((response: SpotlightWorkersResponse) => {
                setSpotlightWorkers(response.workers);
                setVettedWorkersCount(response.vetted_workers_count);
            })
            .catch(consoleLogInDev)
            .finally(() => {
                setIsLoading(false);
            });
    }, [searchedZipCode, queryFilters]);

    function updateZipCode(event: React.ChangeEvent<HTMLInputElement>) {
        if (event.target.value.length <= 5 && !isNaN(Number(event.target.value))) {
            setZipCode(event.target.value.trim());
            setFilters(defaultFilters);
        }
    }

    function getEncodedFiltersQueryString(filters: StaffFilterOptions) {
        return filters.education || filters.experience ? `&filters=${btoa(JSON.stringify(filters))}` : '';
    }

    function onSearch(newFilters: StaffFilterOptions) {
        setFilters(newFilters);
        const url = `/worker-search?zip=${zipCode}${getEncodedFiltersQueryString(newFilters)}`;
        history.push(url);
        track('Customer Searched for Staff Near Them');
    }

    if (searchedZipCode) {
        return (
            <div style={{ backgroundColor: Colors.white, width: '100%' }}>
                <StaffNearYou
                    signUp={signUp}
                    zipCode={zipCode}
                    searchedZipCode={searchedZipCode}
                    updateZipCode={updateZipCode}
                    onSearch={onSearch}
                    loading={isLoading}
                    workers={spotlightWorkers}
                    vettedWorkersCount={vettedWorkersCount}
                    filters={filters}
                />
            </div>
        );
    }

    return (
        <HeroContainer>
            <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                style={{
                    maxWidth: 1280,
                    margin: '0 auto',
                    minHeight: smDown ? 'auto' : '85vh',
                    paddingTop: smDown ? 48 : 0,
                    paddingBottom: smDown ? 48 : 0,
                }}
            >
                <Grid
                    item
                    xs={12}
                    md={6}
                    style={{
                        paddingRight: smDown ? 0 : 48,
                        marginBottom: smDown ? 40 : 0,
                    }}
                >
                    <Text
                        variant="caption"
                        bold
                        textStyle={{
                            color: Colors.turquoise,
                            textTransform: 'uppercase',
                            letterSpacing: 2,
                            fontSize: 13,
                            marginBottom: 16,
                        }}
                    >
                        THE #1 STAFFING PLATFORM FOR CHILD CARE
                    </Text>
                    <Text
                        variant="h1"
                        bold
                        textStyle={{
                            color: Colors.darkNavy,
                            fontSize: smDown ? 36 : 48,
                            lineHeight: smDown ? '44px' : '58px',
                            letterSpacing: '-0.02em',
                            marginBottom: 20,
                        }}
                    >
                        Staff your child care center in minutes.
                    </Text>
                    <Text
                        variant="body1"
                        textStyle={{
                            color: Colors.mediumGrey,
                            fontSize: smDown ? 16 : 18,
                            lineHeight: smDown ? '24px' : '28px',
                            maxWidth: 480,
                            marginBottom: 32,
                        }}
                    >
                        Never scramble for child care staff again. Quickly book vetted, qualified substitutes through
                        Tandem, the trusted platform for child care staffing.
                    </Text>
                    <TrustList>
                        <TrustItem text="Fully compliant, state-licensed staff" />
                        <TrustItem text="Verified child care credentials" />
                        <TrustItem text="Flexible short-term or full-time support" />
                    </TrustList>
                </Grid>
                <Grid item xs={12} md={5}>
                    <SearchCard>
                        <HomeFindSection zipCode={zipCode} updateZipCode={updateZipCode} onSearch={onSearch} />
                    </SearchCard>
                </Grid>
            </Grid>
        </HeroContainer>
    );
}

const HeroContainer = styled('div')({
    backgroundColor: Colors.white,
    paddingLeft: 80,
    paddingRight: 80,
    width: '100%',
    [theme.breakpoints.down('md')]: {
        paddingLeft: 20,
        paddingRight: 20,
    },
});

const SearchCard = styled('div')({
    backgroundColor: Colors.white,
    borderRadius: 20,
    padding: '36px 28px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
});

const TrustList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
});

const TrustRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
});
